import * as React from 'react';
import { Image, Text } from '@blueprint/ui';
import {
    AgencyLogo,
    CardsContainer,
    CardDetails,
    CardHeader,
    PriceContainer,
    PriceTitleText,
    MetaCard,
    StyledTitle,
    StyledCard,
    StyledLink
} from './SimilarListingCards.styles';

import Link from 'next/link';
import { ListingFeatureList } from '@zoopla/listings/features';
import { ListingImage } from '@zoopla/listings/image';
import { SimilarListingCardProps } from './SimilarListingCards.types';

type SimilarListingCardsProps = {
    listings: SimilarListingCardProps[];
    metaCardAfter?: React.ReactElement;
    trackSimilarListingClick: (index: number) => void;
    cardContainerRef?: React.Ref<HTMLDivElement>;
    onScroll?: () => void;
};

export const SimilarListingCards = ({
    listings,
    metaCardAfter,
    trackSimilarListingClick,
    cardContainerRef,
    onScroll
}: SimilarListingCardsProps) => {
    return (
        <CardsContainer onScroll={onScroll} ref={cardContainerRef}>
            {listings.map((listing, index) => {
                const {
                    displayAddress,
                    branch,
                    featurePreview,
                    imagePreview,
                    price,
                    pricingQualifier,
                    title,
                    listingDetailUri,
                    statusSummary
                } = listing;

                return listingDetailUri ? (
                    <Link
                        passHref
                        prefetch={false}
                        href={listingDetailUri}
                        key={`${listing.listingId}-${index}`}
                    >
                        <StyledLink
                            data-testid="similar-property-card"
                            onClick={() => trackSimilarListingClick(index)}
                        >
                            <StyledCard
                                hasShadow={false}
                                hasBorder
                                hasBackground
                            >
                                <ListingImage
                                    image={imagePreview}
                                    flag={statusSummary}
                                />
                                <CardDetails>
                                    <CardHeader>
                                        <PriceContainer>
                                            {pricingQualifier && (
                                                <PriceTitleText size="secondary">
                                                    {pricingQualifier}
                                                </PriceTitleText>
                                            )}
                                            <Text
                                                size="6"
                                                isBold
                                                data-testid={`price-${index}`}
                                            >
                                                {price}
                                            </Text>
                                        </PriceContainer>

                                        <AgencyLogo>
                                            {branch?.logoUrl ? (
                                                <Image
                                                    src={branch?.logoUrl}
                                                    alt={`Marketed by ${branch?.name}`}
                                                />
                                            ) : null}
                                        </AgencyLogo>
                                    </CardHeader>
                                    <ListingFeatureList
                                        features={featurePreview}
                                    />
                                    {title && (
                                        <StyledTitle size="8">
                                            {title}
                                        </StyledTitle>
                                    )}
                                    <Text size="secondary" color={'text-body'}>
                                        {displayAddress}
                                    </Text>
                                </CardDetails>
                            </StyledCard>
                        </StyledLink>
                    </Link>
                ) : null;
            })}
            {Boolean(metaCardAfter) && (
                <MetaCard data-testid="meta-card">{metaCardAfter}</MetaCard>
            )}
        </CardsContainer>
    );
};
